import type {
  ARMDetails,
  Country,
  MortgageTermsData,
  MortgageType,
  VariableDetails,
} from '../types/mortgageTypes';

const CANADA_PRIME_DEFAULT = 4.45;
const CANADA_DISCOUNT_DEFAULT = 1.1;
const USA_PRIME_DEFAULT = 8.5;
const USA_DISCOUNT_DEFAULT = 0.5;

const DEFAULT_US_ARM_DETAILS: ARMDetails = {
  initialFixedPeriod: 5,
  adjustmentFrequency: 12,
  indexRate: 3.5,
  margin: 2.5,
  initialAdjustmentCap: 2,
  periodicCap: 2,
  lifetimeCap: 5,
};

export function canadaVariableDefaults(rate?: number): VariableDetails {
  const primeRate = CANADA_PRIME_DEFAULT;
  const discount =
    rate !== undefined && Number.isFinite(rate)
      ? Number((primeRate - rate).toFixed(2))
      : CANADA_DISCOUNT_DEFAULT;
  return {
    primeRate,
    discount,
    variableType: 'arm',
    triggerRate: undefined,
  };
}

function usaVariableDefaults(): VariableDetails {
  const primeRate = USA_PRIME_DEFAULT;
  const discount = USA_DISCOUNT_DEFAULT;
  return {
    primeRate,
    discount,
    variableType: 'arm',
    triggerRate: undefined,
  };
}

/**
 * Maps stored mortgage terms to the three-way toggle value shown in the UI.
 */
export function getMortgageTypeToggleValue(terms: MortgageTermsData): MortgageType {
  if (terms.country === 'Canada' && terms.mortgageType === 'variable') {
    if (terms.variableDetails?.variableType === 'vrm') return 'variable';
    return 'adjustable';
  }
  return terms.mortgageType;
}

/**
 * Applies a mortgage-type toggle selection to terms (Canada maps ARM/Variable to variable + subtype).
 */
export function applyMortgageTypeToggle(
  terms: MortgageTermsData,
  toggle: MortgageType
): MortgageTermsData {
  if (terms.country === 'Canada') {
    if (toggle === 'fixed') {
      return {
        ...terms,
        mortgageType: 'fixed',
        armDetails: undefined,
        variableDetails: undefined,
      };
    }

    if (toggle === 'adjustable') {
      const variableDetails: VariableDetails = terms.variableDetails
        ? { ...terms.variableDetails, variableType: 'arm' }
        : canadaVariableDefaults(terms.rate);
      const rate = Number((variableDetails.primeRate - variableDetails.discount).toFixed(2));
      return {
        ...terms,
        mortgageType: 'variable',
        armDetails: undefined,
        variableDetails,
        rate,
      };
    }

    if (toggle === 'variable') {
      const variableDetails: VariableDetails = terms.variableDetails
        ? { ...terms.variableDetails, variableType: 'vrm' }
        : { ...canadaVariableDefaults(terms.rate), variableType: 'vrm' };
      const rate = Number((variableDetails.primeRate - variableDetails.discount).toFixed(2));
      return {
        ...terms,
        mortgageType: 'variable',
        armDetails: undefined,
        variableDetails,
        rate,
      };
    }
  }

  const updated: MortgageTermsData = { ...terms, mortgageType: toggle };

  if (toggle === 'adjustable' && !updated.armDetails) {
    updated.armDetails = { ...DEFAULT_US_ARM_DETAILS };
    updated.variableDetails = undefined;
  } else if (toggle === 'variable' && !updated.variableDetails) {
    const variableDetails = usaVariableDefaults();
    updated.variableDetails = variableDetails;
    updated.rate = Number((variableDetails.primeRate - variableDetails.discount).toFixed(2));
    updated.armDetails = undefined;
  } else if (toggle === 'fixed') {
    updated.armDetails = undefined;
    updated.variableDetails = undefined;
  }

  return updated;
}

/**
 * Ensures Canada never uses US adjustable (index/margin) semantics. Idempotent for valid state.
 */
export function normalizeMortgageTerms(terms: MortgageTermsData): MortgageTermsData {
  if (terms.country !== 'Canada') {
    return terms;
  }

  if (terms.mortgageType === 'adjustable') {
    const variableDetails: VariableDetails = terms.variableDetails
      ? { ...terms.variableDetails, variableType: 'arm' }
      : canadaVariableDefaults(terms.rate);
    const rate = Number((variableDetails.primeRate - variableDetails.discount).toFixed(2));
    return {
      ...terms,
      mortgageType: 'variable',
      armDetails: undefined,
      variableDetails,
      rate,
    };
  }

  if (terms.mortgageType === 'variable') {
    if (!terms.variableDetails) {
      const variableDetails = { ...canadaVariableDefaults(terms.rate), variableType: 'vrm' as const };
      const rate = Number((variableDetails.primeRate - variableDetails.discount).toFixed(2));
      return { ...terms, armDetails: undefined, variableDetails, rate };
    }
    return { ...terms, armDetails: undefined };
  }

  return { ...terms, armDetails: undefined };
}

export function getMortgageTypeSummaryLabel(terms: MortgageTermsData): string {
  const toggle = getMortgageTypeToggleValue(terms);
  if (toggle === 'adjustable') return 'ARM';
  return toggle.charAt(0).toUpperCase() + toggle.slice(1);
}

export function migrateCountrySwitch(
  terms: MortgageTermsData,
  newCountry: Country
): MortgageTermsData {
  let updated: MortgageTermsData = { ...terms, country: newCountry };

  if (newCountry === 'Canada') {
    updated = normalizeMortgageTerms(updated);
    if (!updated.canadianTermDetails) {
      updated.canadianTermDetails = {
        termLength: 5,
        showRenewalScenario: false,
        renewalRate: undefined,
        renewalRates: undefined,
      };
    }
    return updated;
  }

  updated.canadianTermDetails = undefined;
  if (
    updated.mortgageType === 'variable' &&
    updated.variableDetails?.variableType === 'arm'
  ) {
    updated = {
      ...updated,
      mortgageType: 'adjustable',
      armDetails: updated.armDetails ?? { ...DEFAULT_US_ARM_DETAILS },
      variableDetails: undefined,
    };
  }
  return updated;
}
